import { NavLink } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Users,
  Settings,
  Sparkles,
  UserCircle,
  CreditCard,
  MessageSquareText,
  Library,
  Newspaper,
  UserCog,
  Trash2,
  Package,
  PackagePlus,
  ShoppingBag,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface NavItem {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  children?: Array<{ name: string; href: string; icon: typeof LayoutDashboard; end?: boolean }>;
}

interface SidebarProps {
  isDesktop: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  {
    name: 'Users',
    href: '/dashboard/users',
    icon: Users,
    children: [
      // `end` so this only highlights on the exact /dashboard/users route, not
      // on its descendant /dashboard/users/deletion-requests.
      { name: 'User Management', href: '/dashboard/users', icon: UserCog, end: true },
      {
        name: 'Deletion Requests',
        href: '/dashboard/users/deletion-requests',
        icon: Trash2,
      },
    ],
  },
  { name: 'Personas', href: '/dashboard/personas', icon: UserCircle },
  { name: 'Blog', href: '/dashboard/blog', icon: Newspaper },
  {
    name: 'Products',
    href: '/dashboard/products',
    icon: ShoppingBag,
    children: [
      // `end` so Offerings only highlights on the exact route, not on /addons.
      { name: 'Offerings', href: '/dashboard/products', icon: Package, end: true },
      { name: 'Add-ons', href: '/dashboard/products/addons', icon: PackagePlus },
    ],
  },
  { name: 'Story Cards', href: '/dashboard/story-cards', icon: CreditCard },
  {
    name: 'AI Playground',
    href: '/dashboard/ai-playground/interview',
    icon: Sparkles,
    children: [
      {
        name: 'Life-Story Interview',
        href: '/dashboard/ai-playground/interview',
        icon: MessageSquareText,
      },
      {
        name: 'Memory Archive',
        href: '/dashboard/ai-playground/memory-archive',
        icon: Library,
      },
    ],
  },
];

export function Sidebar({ isDesktop, isOpen, onToggle, onNavigate }: SidebarProps) {
  const showIconRail = isDesktop && !isOpen;

  return (
    <div className="fixed left-0 top-0 z-40 h-screen">
      {!isDesktop && isOpen && (
        <button
          type="button"
          aria-label="Close navigation menu"
          className="fixed inset-0 bg-background/60 backdrop-blur-sm"
          onClick={onToggle}
        />
      )}

      <aside
        className={cn(
          'relative h-screen border-r border-border bg-card transition-[width,transform] duration-300 ease-in-out',
          isDesktop ? (isOpen ? 'w-64' : 'w-20 translate-x-0') : 'w-[17rem] sm:w-64',
          !isDesktop && (isOpen ? 'translate-x-0' : '-translate-x-[calc(100%-2rem)]')
        )}
      >
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onToggle}
          aria-label={isOpen ? 'Collapse navigation menu' : 'Expand navigation menu'}
          aria-expanded={isOpen}
          className="absolute right-0 top-4 z-10 h-9 w-9 translate-x-1/2 rounded-full border-border bg-background shadow-sm hover:bg-accent sm:top-6 sm:h-10 sm:w-10"
        >
          {isOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>

        <div className="flex h-full flex-col bg-card">
          {/* Logo */}
          <div
            className={cn(
              'flex h-16 items-center border-b border-border px-5 sm:px-6',
              showIconRail ? 'justify-center' : 'gap-2'
            )}
          >
            <img src="/logos/orb-mark.png" alt="LegacyOrb" className="h-8 w-8" />
            <span
              className={cn(
                'font-display text-xl font-bold tracking-tight text-foreground',
                showIconRail && 'hidden'
              )}
            >
              LegacyOrb
            </span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
            {navigation.map((item) => (
              <div key={item.name} className="space-y-1">
                <NavLink
                  to={item.href}
                  end={item.href === '/dashboard'}
                  onClick={onNavigate}
                  title={showIconRail ? item.name : undefined}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      showIconRail && 'justify-center px-0',
                      // For a group parent with children, we highlight it only when the route is
                      // exactly the parent target — children have their own active style below.
                      isActive && !item.children
                        ? 'bg-orb-gold text-white'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )
                  }
                >
                  <item.icon className="h-5 w-5" />
                  <span className={cn(showIconRail && 'hidden')}>{item.name}</span>
                </NavLink>
                {item.children && !showIconRail && (
                  <div className="ml-4 space-y-1 border-l border-border pl-3">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.name}
                        to={child.href}
                        end={child.end}
                        onClick={onNavigate}
                        title={showIconRail ? child.name : undefined}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                            isActive
                              ? 'bg-orb-gold/15 font-medium text-orb-teal'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          )
                        }
                      >
                        <child.icon className="h-4 w-4" />
                        {child.name}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Settings at bottom */}
          <div className="border-t border-border px-3 py-4">
            <NavLink
              to="/dashboard/settings"
              end
              onClick={onNavigate}
              title={showIconRail ? 'Settings' : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  showIconRail && 'justify-center px-0',
                  isActive
                    ? 'bg-orb-gold text-white'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )
              }
            >
              <Settings className="h-5 w-5" />
              <span className={cn(showIconRail && 'hidden')}>Settings</span>
            </NavLink>
          </div>
        </div>
      </aside>
    </div>
  );
}
